import authService from '@/services/authService'
import permissionsService from '@/services/permissionsService'
import { authManager } from '@/utils/securityUtils'
import { clearCachedUserRole } from '@/services/roleRoutingService'
import { tokenService } from '@/services/tokenService'

const authModule = {
  namespaced: true,
 
  state: {
    currentUser: null,
    isAuthenticated: false,                
    sessionToken: null,
    loading: false,
    error: null,
    otpUser: null, // Store user info during OTP flow
    requiresOtp: false
  },
 
  getters: {
    currentUser: state => state.currentUser,
    isAuthenticated: state => state.isAuthenticated,
    sessionToken: state => state.sessionToken,
    isLoading: state => state.loading,
    hasError: state => !!state.error,
    error: state => state.error,
    otpUser: state => state.otpUser,
    requiresOtp: state => state.requiresOtp
  },
 
  mutations: {
    SET_AUTH(state, { user, token }) {
      state.currentUser = user
      state.sessionToken = token
      state.isAuthenticated = true
      state.requiresOtp = false
      state.otpUser = null
      if (token) {
        // Store access token in localStorage so all API clients (which read
        // localStorage.getItem('session_token')) can attach the Authorization header.
        tokenService.setToken(token, false) // false = use localStorage
        localStorage.setItem('current_user', JSON.stringify(user))
      }
      // Store tenant_id separately for profile and other tenant-scoped requests
      if (user?.tenant_id) {
        localStorage.setItem('tenant_id', String(user.tenant_id))
      }
      try {
        sessionStorage.removeItem('tprm_mfa_pending')
      } catch (e) { /* ignore */ }
      // Clear permission cache on login to force fresh permission checks

      permissionsService.clearCache()
    },
   
    SET_OTP_REQUIRED(state, { user, requiresOtp }) {
      state.otpUser = user
      state.requiresOtp = requiresOtp
      state.isAuthenticated = false
      // Stale tokens make authService.isAuthenticated() true and the router kicks users off /otp-verification
      // Use tokenService to clear all auth tokens
      tokenService.clearTokens()
      localStorage.removeItem('current_user')
      try {
        sessionStorage.setItem('tprm_mfa_pending', '1')
      } catch (e) { /* ignore */ }
    },

    SET_PASSWORD_CHANGE_REQUIRED(state, { user }) {
      state.otpUser = user
      state.requiresOtp = false
      state.isAuthenticated = false
      // Use tokenService to clear all auth tokens
      tokenService.clearTokens()
      localStorage.removeItem('current_user')
    },
   
    CLEAR_AUTH(state, options = {}) {
      const skipPermissionCache = Boolean(options && options.skipPermissionCache)
      state.currentUser = null
      state.sessionToken = null
      state.isAuthenticated = false
      state.otpUser = null
      state.requiresOtp = false
      // Use tokenService to clear all auth tokens
      tokenService.clearTokens()
      localStorage.removeItem('current_user')
      localStorage.removeItem('tenant_id')
      clearCachedUserRole()
      try {
        sessionStorage.removeItem('tprm_mfa_pending')
      } catch (e) { /* ignore */ }
      if (!skipPermissionCache) {

        permissionsService.clearCache()
      }
    },
   
    SET_LOADING(state, loading) {
      state.loading = loading
    },
   
    SET_ERROR(state, error) {
      state.error = error
    },
   
    CLEAR_ERROR(state) {
      state.error = null
    },
   
    SET_USER(state, user) {
      state.currentUser = user
      if (user) {
        localStorage.setItem('current_user', JSON.stringify(user))
      }
    }
  },
 
  actions: {
    /**
     * Step 1: Login with username and password
     * This will trigger OTP to be sent, or require password change for first-time login
     */
    async login({ commit }, { username, password }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const result = await authService.login(username, password)

        if (result.success) {
          if (result.requiresPasswordChange) {
            commit('SET_PASSWORD_CHANGE_REQUIRED', { user: result.user })
            return {
              success: true,
              requiresPasswordChange: true,
              message: result.message,
              user: result.user
            }
          }
          if (result.requiresOtp) {
            // OTP required, store user info for OTP verification
            commit('SET_OTP_REQUIRED', {
              user: result.user,
              requiresOtp: true
            })

            return {
              success: true,
              requiresOtp: true,
              message: result.message,
              user: result.user
            }
          } else {
            // Direct login without OTP (shouldn't happen with MFA)
            commit('SET_AUTH', {
              user: result.user,
              token: result.data.session_token
            })
            authManager.lastActivity = Date.now()
            authManager.startIdleMonitoring()
            return {
              success: true,
              requiresOtp: false,
              user: result.user
            }
          }
        } else {
          commit('SET_ERROR', result.error)
          return {
            success: false,
            error: result.error
          }
        }
      } catch (error) {

        const errorMessage = error.message || 'An unexpected error occurred during login'
        commit('SET_ERROR', errorMessage)
        return {
          success: false,
          error: errorMessage
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * First-time login: set permanent password, then trigger OTP
     */
    async setFirstPassword({ commit }, { username, tempPassword, newPassword }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const result = await authService.setFirstPassword(username, tempPassword, newPassword)

        if (result.success) {
          if (result.requiresOtp) {
            commit('SET_OTP_REQUIRED', {
              user: result.user,
              requiresOtp: true
            })
            return {
              success: true,
              requiresOtp: true,
              message: result.message,
              user: result.user
            }
          } else {
            commit('SET_AUTH', {
              user: result.user,
              token: result.data.session_token
            })
            authManager.lastActivity = Date.now()
            authManager.startIdleMonitoring()
            return {
              success: true,
              requiresOtp: false,
              user: result.user
            }
          }
        } else {
          commit('SET_ERROR', result.error)
          return {
            success: false,
            error: result.error
          }
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to update password'
        commit('SET_ERROR', errorMessage)
        return {
          success: false,
          error: errorMessage
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Step 2: Verify OTP code
     */
    async verifyOtp({ commit, state }, otp) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
       
        if (!state.otpUser) {
          const error = 'No user found for OTP verification. Please login again.'
          commit('SET_ERROR', error)
          return { success: false, error }
        }

        const result = await authService.verifyOtp(state.otpUser.username, otp)
       
        if (result.success) {
          commit('SET_AUTH', {
            user: result.user,
            token: result.token
          })
          authManager.lastActivity = Date.now()
          authManager.startIdleMonitoring()

          return {
            success: true,
            user: result.user
          }
        } else {
          commit('SET_ERROR', result.error)
          return {
            success: false,
            error: result.error
          }
        }
      } catch (error) {

        const errorMessage = error.message || 'OTP verification failed'
        commit('SET_ERROR', errorMessage)
        return {
          success: false,
          error: errorMessage
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },
   
    /**
     * Resend OTP code
     */
    async resendOtp({ commit, state }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
       
        if (!state.otpUser) {
          const error = 'No user found for OTP resend. Please login again.'
          commit('SET_ERROR', error)
          return { success: false, error }
        }

        const result = await authService.resendOtp(state.otpUser.username)
       
        if (result.success) {
          return {
            success: true,
            message: result.message
          }
        } else {
          commit('SET_ERROR', result.error)
          return {
            success: false,
            error: result.error
          }
        }
      } catch (error) {

        const errorMessage = error.message || 'Failed to resend OTP'
        commit('SET_ERROR', errorMessage)
        return {
          success: false,
          error: errorMessage
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },
   
    /**
     * Validate current session
     */
    async validateSession({ commit }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')
       
        // Check if we have a token
        if (!authService.isAuthenticated()) {
          commit('CLEAR_AUTH')
          return { success: false, error: 'No authentication found' }
        }

        const result = await authService.validateSession()
       
        if (result.success) {
          const token = authService.getSessionToken()
          commit('SET_AUTH', {
            user: result.user,
            token: token
          })

          return {
            success: true,
            user: result.user
          }
        } else {
          commit('CLEAR_AUTH')
          return {
            success: false,
            error: result.error
          }
        }
      } catch (error) {

        commit('CLEAR_AUTH')
        return {
          success: false,
          error: error.message || 'Session validation failed'
        }
      } finally {
        commit('SET_LOADING', false)
      }
    },
   
    /**
     * Logout user
     */
    async logoutUser({ commit }) {
      try {
        commit('SET_LOADING', true)

        const result = await authService.logout()

        // Always clear auth state from Vuex
        commit('CLEAR_AUTH')
        authManager.stopIdleMonitoring()

        if (result.success) {

          return { success: true, message: result.message }
        } else {

          return { success: false, error: result.error }
        }
      } catch (error) {

        // Still clear auth even if API call fails
        commit('CLEAR_AUTH')
        authManager.stopIdleMonitoring()
        return { success: false, error: error.message }
      } finally {
        commit('SET_LOADING', false)
      }
    },
   
    /**
     * Initialize auth state from localStorage
     */
    initializeAuth({ commit }) {
      try {
        const user = authService.getCurrentUser()
        const token = authService.getSessionToken()
       
        if (user && token) {
          commit('SET_AUTH', { user, token })

        } else {
          // Fresh visit / no session: do not treat as logout or wipe permission cache
          commit('CLEAR_AUTH', { skipPermissionCache: true })

        }
      } catch (error) {

        commit('CLEAR_AUTH', { skipPermissionCache: true })
      }
    },
   
    /**
     * SSO: Initiate SSO login (Microsoft or Google)
     */
    async initiateSSO({ commit }, { provider, redirectUri }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const result = await authService.getSsoAuthorizeUrl(provider, redirectUri)

        if (result.success) {
          return {
            success: true,
            authorizeUrl: result.authorizeUrl,
            redirectUri: result.redirectUri,
            provider: result.provider
          }
        } else {
          commit('SET_ERROR', result.error)
          return { success: false, error: result.error }
        }
      } catch (error) {
        const errorMessage = error.message || 'Failed to initiate SSO'
        commit('SET_ERROR', errorMessage)
        return { success: false, error: errorMessage }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * SSO: Handle the callback after SSO provider redirects back
     */
    async handleSSOCallback({ commit }, { provider, code, redirectUri }) {
      try {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        const result = await authService.ssoCallback(provider, code, redirectUri)

        if (result.success) {
          commit('SET_AUTH', {
            user: result.user,
            token: result.token
          })
          return { success: true, user: result.user }
        } else {
          commit('SET_ERROR', result.error)
          return { success: false, error: result.error }
        }
      } catch (error) {
        const errorMessage = error.message || 'SSO authentication failed'
        commit('SET_ERROR', errorMessage)
        return { success: false, error: errorMessage }
      } finally {
        commit('SET_LOADING', false)
      }
    },

    /**
     * Clear error message
     */
    clearError({ commit }) {
      commit('CLEAR_ERROR')
    }
  }
}
 
export default authModule